import type { ComponentProps } from 'react';
import { cn, Checkbox as HeritageCheckbox } from '@herit-fe/heritage-ui';
import { CONTROL_BOX } from './form-control-style';

/**
 * 체크박스 — heritage `Checkbox`(base-ui) 위에 **indeterminate(부분선택) 스타일 + 치수 보강**.
 * 치수·모양은 `CONTROL_BOX`(라디오와 공유) + `rounded-full`(원형 체크박스 디자인).
 *
 * heritage 는 indeterminate 를 스타일링하지 않아(인디케이터가 항상 CheckIcon · primary fill 은 `data-checked`
 * 에만) 부분선택이 "빈 박스 + 체크"로 어색하게 보인다 → 부분선택 시 **primary fill + 대시(체크 숨김)**로 의미를
 * 분명히 한다(전체선택 헤더의 일부 선택 등). 외부 npm 이라 직접 수정 불가 → wrapper 로 className 보강.
 *
 * `data-indeterminate` = base-ui Checkbox.Root 의 상태 속성. 체크 svg 는 숨기고, 대시는 `before` 의사요소를
 * Root 의 flex 중앙정렬에 실어 그린다(`bg-current` = primary-foreground).
 */
const INDETERMINATE_CLASS = [
  'data-[indeterminate]:border-primary',
  'data-[indeterminate]:bg-primary',
  'data-[indeterminate]:text-primary-foreground',
  '[&[data-indeterminate]_svg]:hidden',
  'data-[indeterminate]:before:block',
  'data-[indeterminate]:before:h-[2px]',
  'data-[indeterminate]:before:w-2.5',
  'data-[indeterminate]:before:rounded-full',
  'data-[indeterminate]:before:bg-current',
  "data-[indeterminate]:before:content-['']",
].join(' ');

/**
 * 체크 아이콘 크기·굵기 — 박스를 키운 만큼 heritage 기본(14px)도 같이 키우고(박스 대비 비율 유지),
 * 획은 lucide 기본 2 → 3 으로 굵힌다(원형 큰 박스에서 얇은 체크가 빈약해 보임).
 * heritage 가 indicator 에 `[&>svg]:size-3.5` 를 직접 얹어 두어, 같은 명시도의 `[&_svg]` 로는
 * 생성 순서에 따라 지는 수가 있다 → **indicator slot 을 한 단계 더 짚어 명시도로 이긴다**
 * (`radio-group-item.tsx` 의 선택 점 보강과 동일 수법). 획은 lucide 가 `stroke-width` 를 presentation
 * attribute 로 주므로 CSS 면 무조건 이긴다.
 */
const ICON_CLASS = [
  '[&_[data-slot=checkbox-indicator]>svg]:size-4',
  '[&_[data-slot=checkbox-indicator]>svg]:stroke-[3]',
].join(' ');

export function Checkbox({ className, ...props }: ComponentProps<typeof HeritageCheckbox>) {
  return (
    <HeritageCheckbox
      className={cn(CONTROL_BOX, 'rounded-md', ICON_CLASS, INDETERMINATE_CLASS, className)}
      {...props}
    />
  );
}
